import { router } from "@inertiajs/react";
import { LoaderIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	type ReasonOption,
	reasonLabel,
} from "@/lib/appointments/reason-label";
import { cn } from "@/lib/utils";

export type MitraReasonProp = {
	reasons: Record<string, string | null>;
	options: ReasonOption[];
} | null;

/** The order's one reason, which reschedules reuse instead of asking again. */
export const orderMitraReason = (prop: MitraReasonProp | undefined) =>
	Object.values(prop?.reasons ?? {}).find(Boolean) ?? null;

export function MitraAssignmentReasonSelect({
	id,
	options,
	value,
	onChange,
	invalid,
	disabled,
}: {
	id?: string;
	options: ReasonOption[];
	value: string | null | undefined;
	onChange: (value: string) => void;
	invalid?: boolean;
	disabled?: boolean;
}) {
	const { t, i18n } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.fields.mitra_assignment_reason",
	});

	return (
		<Select onValueChange={onChange} value={value ?? ""} disabled={disabled}>
			<SelectTrigger
				id={id}
				aria-invalid={invalid}
				className="h-auto min-h-9 w-full min-w-0 text-left shadow-inner bg-sidebar [&>span]:min-w-0 [&>span]:!whitespace-normal [&>span]:!line-clamp-none [&>svg]:shrink-0"
			>
				<SelectValue placeholder={t("placeholder")} />
			</SelectTrigger>
			{/* The reasons are full sentences: the trigger and the items wrap rather than
			    clip, and the trigger never pushes past the dialog that holds it. */}
			<SelectContent className="max-w-[--radix-select-trigger-width]">
				{options.map((reason) => (
					<SelectItem
						key={reason.value}
						value={reason.value}
						className="whitespace-normal"
					>
						{reasonLabel(reason, i18n.language)}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
}

/** The reason field with its label and message. `id` lands on the trigger, so callers can focus it. */
export function MitraAssignmentReasonBox({
	id,
	label,
	description,
	options,
	value,
	onChange,
	error,
	className,
}: {
	id: string;
	label: string;
	description?: string;
	options: ReasonOption[];
	value: string | null | undefined;
	onChange: (value: string) => void;
	error?: string;
	className?: string;
}) {
	return (
		<div
			className={cn(
				"grid gap-2 border-l-[3px] border-l-primary pl-3 motion-preset-rebound-down",
				className,
			)}
		>
			<Label htmlFor={id}>{label}</Label>
			<MitraAssignmentReasonSelect
				id={id}
				options={options}
				value={value}
				onChange={onChange}
				invalid={!!error}
			/>
			{error ? (
				<p className="text-xs font-medium text-destructive">{error}</p>
			) : (
				description && (
					<p className="text-xs text-muted-foreground text-pretty">
						{description}
					</p>
				)
			)}
		</div>
	);
}

export type MitraReasonVisit = {
	appointmentId: string | null;
	visitNumber: number;
	reason: string | null;
};

export interface MitraAssignmentReasonTarget {
	url: string;
	visits: MitraReasonVisit[];
}

export function MitraAssignmentReasonDialog({
	target,
	options,
	onClose,
}: {
	target: MitraAssignmentReasonTarget | null;
	options: ReasonOption[];
	onClose: () => void;
}) {
	const { t } = useTranslation("appointments");
	const [value, setValue] = useState("");
	const [saving, setSaving] = useState(false);
	// `disabled={saving}` engages only after a re-render, so a second click in the
	// same tick still sends a second PUT and writes duplicate history rows.
	const isSubmittingRef = useRef(false);

	useEffect(() => {
		setValue(target?.visits.find((visit) => visit.reason)?.reason ?? "");
	}, [target]);

	// One reason for the order: saving writes it to every listed visit that differs.
	const changed = value
		? (target?.visits ?? []).filter((visit) => visit.reason !== value)
		: [];

	const save = () => {
		if (!target || isSubmittingRef.current) return;
		isSubmittingRef.current = true;
		setSaving(true);
		router.put(
			target.url,
			{
				reasons: changed.map((visit) => ({
					appointment_id: visit.appointmentId,
					visit_number: visit.visitNumber,
					mitra_assignment_reason: value,
				})),
			},
			{
				preserveScroll: true,
				preserveState: true,
				onStart: () => setSaving(true),
				onSuccess: () => onClose(),
				onFinish: () => {
					isSubmittingRef.current = false;
					setSaving(false);
				},
			},
		);
	};

	return (
		<Dialog
			open={!!target}
			onOpenChange={(open) => {
				if (!open && !saving) onClose();
			}}
		>
			<DialogContent className="sm:max-w-[480px]">
				<DialogHeader>
					<DialogTitle>{t("mitra_reason_dialog.title")}</DialogTitle>
					<DialogDescription>
						{t("mitra_reason_dialog.description")}
					</DialogDescription>
				</DialogHeader>
				<div className="min-w-0 px-0.5 pb-4">
					<MitraAssignmentReasonSelect
						options={options}
						value={value}
						disabled={saving}
						onChange={setValue}
					/>
				</div>
				<DialogFooter>
					<Button variant="outline" disabled={saving} onClick={onClose}>
						{t("mitra_reason_dialog.cancel")}
					</Button>
					<Button disabled={!changed.length || saving} onClick={save}>
						{saving && <LoaderIcon className="animate-spin" />}
						{t("mitra_reason_dialog.save")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
